import React from 'react';
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  OutlinedInput,
  Typography,
  Box,
  Chip
} from '@mui/material';
import dayjs from 'dayjs';

const EditableFormField = ({
  type = 'text',
  label,
  name,
  value,
  onChange,
  isEditing,
  required = false,
  fullWidth = true,
  margin = 'normal',
  options = [],
  multiline = false,
  rows = 1,
  disabled = false,
  error = false,
  helperText = '',
  placeholder = '',
  ...otherProps
}) => {
  if (!isEditing) {
    // View-only mode
    let displayValue = value;
    
    if (type === 'select' && options.length > 0) {
      const option = options.find(opt => opt.value === value || opt.id === value);
      displayValue = option ? option.label : value;
    } else if (type === 'date' && value) {
      displayValue = dayjs(value).format('MM/DD/YYYY');
    } else if (type === 'datetime-local' && value) {
      displayValue = dayjs(value).format('MM/DD/YYYY HH:mm');
    } else if (type === 'multiselect' && Array.isArray(value)) {
      displayValue = value.map(v => {
        const option = options.find(opt => opt.value === v || opt.id === v);
        return option ? option.label : v;
      }).join(', ');
    }

    return (
      <Box sx={{ mb: margin === 'normal' ? 2 : 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            color: value ? 'text.primary' : 'text.disabled'
          }}
        >
          {displayValue || placeholder || 'Not specified'}
        </Typography>
      </Box>
    );
  }

  // Edit mode
  switch (type) {
    case 'select':
      return (
        <FormControl fullWidth={fullWidth} margin={margin} required={required} error={error}>
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select
            labelId={`${name}-label`}
            name={name}
            value={value || ''}
            onChange={onChange}
            input={<OutlinedInput label={label} />}
            disabled={disabled}
            {...otherProps}
          >
            {options.map((option) => (
              <MenuItem key={option.value || option.id} value={option.value || option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'multiselect':
      return (
        <FormControl fullWidth={fullWidth} margin={margin} required={required} error={error}>
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select
            labelId={`${name}-label`}
            name={name}
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            input={<OutlinedInput label={label} />}
            disabled={disabled}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = options.find(opt => opt.value === value || opt.id === value);
                  return (
                    <Chip 
                      key={value} 
                      label={option ? option.label : value} 
                      size="small" 
                    />
                  );
                })}
              </Box>
            )}
            {...otherProps}
          >
            {options.map((option) => (
              <MenuItem key={option.value || option.id} value={option.value || option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'date':
      return (
        <TextField
          label={label}
          name={name}
          value={value || ''}
          onChange={onChange}
          type="date"
          fullWidth={fullWidth}
          margin={margin}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          {...otherProps}
        />
      );

    case 'datetime-local':
      return (
        <TextField
          label={label}
          name={name}
          value={value || ''}
          onChange={onChange}
          type="datetime-local"
          fullWidth={fullWidth}
          margin={margin}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          {...otherProps}
        />
      );

    case 'textarea':
      return (
        <TextField
          label={label}
          name={name}
          value={value || ''}
          onChange={onChange}
          multiline
          rows={rows}
          fullWidth={fullWidth}
          margin={margin}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          {...otherProps}
        />
      );

    default:
      return (
        <TextField
          label={label}
          name={name}
          value={value || ''}
          onChange={onChange}
          type={type}
          fullWidth={fullWidth}
          margin={margin}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          {...otherProps}
        />
      );
  }
};

export default EditableFormField; 